import { FieldArray, useFormikContext, getIn } from "formik";
import { Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { Box, Card, Flex, Text, Button, IconButton } from "@radix-ui/themes";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import { useEntities } from "@/hooks/useCommonQueries";
import { FilterCondition } from "@/types/breeze/Workflow";

// Values match backend FilterEvaluator + QueryFilterBuilder tokens.
const FILTER_OPERATORS = [
  { value: "eq", label: "Equals", hasValue: true },
  { value: "neq", label: "Not Equals", hasValue: true },
  { value: "contains", label: "Contains", hasValue: true },
  { value: "sw", label: "Starts With", hasValue: true },
  { value: "ew", label: "Ends With", hasValue: true },
  { value: "gt", label: "Greater Than", hasValue: true },
  { value: "lt", label: "Less Than", hasValue: true },
  { value: "gte", label: "Greater Than or Equal", hasValue: true },
  { value: "lte", label: "Less Than or Equal", hasValue: true },
  { value: "is_null", label: "Is Null", hasValue: false },
  { value: "is_not_null", label: "Is Not Null", hasValue: false },
];

interface FilterConditionsFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  entityName?: string;
}

export default function FilterConditionsField({
  name,
  label = "Filter Conditions",
  required = false,
  entityName,
}: FilterConditionsFieldProps) {
  const { values, setFieldValue } = useFormikContext<any>();
  const { data: entities } = useEntities();

  // Get the current filter conditions from the form values using getIn for nested paths
  const filterConditions = getIn(values, name) || [];

  // Initialize filter conditions if they don't exist
  if (!filterConditions || !Array.isArray(filterConditions)) {
    setFieldValue(name, []);
  }

  const fieldOptions = useMemo(() => {
    if (!entityName) return [];
    const entity = entities?.find((e) => e.name === entityName);
    return entity?.fields?.map((f) => ({ value: f.name, label: f.label || f.name })) || [];
  }, [entities, entityName]);

  return (
    <Card style={{ contain: "none" }}>
      <Box p="4">
        <Flex direction="column" gap="4">
          <Text size="3" weight="medium">
            {label}
            {required && <Text color="red"> *</Text>}
          </Text>

          <FieldArray
            name={name}
            render={(arrayHelpers) => (
              <Flex direction="column" gap="4">
                {filterConditions.length > 0 ? (
                  filterConditions.map((c: FilterCondition, index: number) => {
                    const hasValue =
                      FILTER_OPERATORS.find((o) => o.value === c?.operator)?.hasValue ?? true;
                    return (
                      <Card key={index} variant="surface" style={{ contain: "none" }}>
                        <Box p="3">
                          <Flex direction="column" gap="3">
                            <Flex justify="between" align="center">
                              <Text size="2" weight="medium">
                                Condition {index + 1}
                              </Text>
                              <IconButton
                                type="button"
                                variant="ghost"
                                color="red"
                                size="1"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </Flex>

                            {fieldOptions.length > 0 ? (
                              <SearchableSelectField
                                name={`${name}.${index}.field`}
                                label="Field"
                                required
                                options={fieldOptions}
                                placeholder="Select field..."
                                searchPlaceholder="Search fields..."
                                variableToggle
                              />
                            ) : (
                              <InputField
                                name={`${name}.${index}.field`}
                                label="Field"
                                required
                              />
                            )}

                            <SelectField
                              name={`${name}.${index}.operator`}
                              label="Operator"
                              required
                              type="string"
                            >
                              {FILTER_OPERATORS.map((op) => (
                                <SelectField.Item key={op.value} value={op.value}>
                                  {op.label}
                                </SelectField.Item>
                              ))}
                            </SelectField>

                            {hasValue && (
                              <InputField
                                name={`${name}.${index}.value`}
                                label="Value"
                                required
                              />
                            )}
                          </Flex>
                        </Box>
                      </Card>
                    );
                  })
                ) : (
                  <></>
                )}

                <Button
                  type="button"
                  variant="soft"
                  size="2"
                  onClick={() =>
                    arrayHelpers.push({
                      field: "",
                      operator: "eq",
                      value: "",
                    })
                  }
                >
                  <Plus size={16} />
                  Add Filter Condition
                </Button>
              </Flex>
            )}
          />
        </Flex>
      </Box>
    </Card>
  );
}
